import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { getMyProfile } from "../../api/userService";
import { brand } from "./theme";

export default function Settings() {
  const { t } = useTranslation(["dcSettings", "dcLayout"]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getMyProfile()
      .then(setProfile)
      .catch(() => setError("loadProfileError"))
      .finally(() => setLoadingProfile(false));
  }, []);

  function handleLogout() {
    logout();
    navigate("/delivry-company/login");
  }

  const fullName = profile?.fullName || user?.fullName;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 3 }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            background: `linear-gradient(135deg, ${brand.emeraldLight}, ${brand.emeraldDeep})`,
            boxShadow: `0 6px 16px ${alpha(brand.emeraldDeep, 0.35)}`,
          }}
        >
          <SettingsRoundedIcon />
        </Avatar>
        <Box>
          <Typography variant="h4">{t("title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("subtitle")}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {t(error)}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              height: 88,
              background: `linear-gradient(135deg, ${alpha(brand.emeraldLight, 0.28)}, ${alpha(brand.amber, 0.24)})`,
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack direction="row" spacing={2.5} alignItems="flex-end" sx={{ mt: "-40px", mb: 2 }}>
              {loadingProfile ? (
                <Skeleton variant="circular" width={80} height={80} sx={{ border: "4px solid #fff" }} />
              ) : (
                <Avatar
                  src={profile?.profileImageUrl || undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: 26,
                    border: "4px solid #fff",
                    boxShadow: 3,
                    bgcolor: "primary.main",
                  }}
                >
                  {(fullName || "D").charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, pb: 0.5 }}>
                {loadingProfile ? (
                  <Skeleton variant="text" width={180} height={28} />
                ) : (
                  <Typography variant="h6" noWrap>
                    {fullName || t("dcLayout:companyFallback")}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InfoField label={t("fullName")} value={fullName} loading={loadingProfile} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InfoField label={t("email")} value={profile?.email || user?.email} loading={loadingProfile} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <InfoField label={t("phone")} value={profile?.phone} loading={loadingProfile} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <LanguagePreferenceCard />

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("sessionTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("sessionSubtitle")}
                </Typography>
              </Box>
              <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                {t("logOut")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

function LanguagePreferenceCard() {
  const { t } = useTranslation("dcSettings");
  const { language, setLanguage, languages } = useLanguage();

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded" sx={{ bgcolor: "action.hover", color: "text.secondary", width: 40, height: 40 }}>
              <LanguageRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("languageTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("languageSubtitle")}
              </Typography>
            </Box>
          </Stack>
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={(e, value) => value && setLanguage(value)}
            aria-label={t("languageTitle")}
          >
            {Object.values(languages).map((lang) => (
              <ToggleButton key={lang.code} value={lang.code} lang={lang.code}>
                {lang.nativeLabel}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </CardContent>
    </Card>
  );
}

function InfoField({ label, value, loading }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider", minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width="70%" />
      ) : (
        <Typography variant="body2" noWrap>
          {value || "-"}
        </Typography>
      )}
    </Box>
  );
}
